import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { useTheme } from "../context/ThemeContext";
import { getPageBackground, getCardBackground, getPrimaryText, getSecondaryText, getMutedText } from "../utils/themeUtils";
import { Button, Input } from "../components/ui";
import config from "../api/config";
import { getHeaders } from "../api/apiHelpers";
import { useDispatch } from "react-redux";
import { setUser, setAuthzFromCapabilities, setIsMember, setIsStaff } from "../store/slices/authSlice";
import { getCapabilities } from "../api/authAPI";
import { isStaffRole, isMemberRole, isAdminOrStaffRole } from "../utils/roles";
import { resolveStaffHomeRoute, staffHomeRoutePath } from "../utils/staffHomeRoute";
import { safeSetItem } from "@mediatechgroup/app-version";
import { PASSWORD_RULE_TEXT, passwordProblem } from "../utils/passwordRule";

/**
 * The reset email carries one of two kinds of link (backend:
 * services/recoveryReset.js):
 *   - the normal one, `?token=…` (the app's own reset token), or
 *   - the fallback one, sent when the first attempt to save the password
 *     failed: a short-lived recovery session in the URL fragment
 *     (`#access_token=…&refresh_token=…&type=recovery`).
 * Until 2026-10-08 this page only understood the first, so the fallback link
 * always ended on "No reset token provided".
 */
interface RecoveryLink {
  accessToken: string;
  refreshToken: string;
  /** Set when the link arrived carrying an error instead of a session. */
  linkError: string;
}

export const readRecoveryLink = (hash: string): RecoveryLink | null => {
  const raw = (hash || "").replace(/^#/, "");
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const accessToken = params.get("access_token") || "";
  const linkError = params.get("error_description") || params.get("error") || "";
  if (linkError && !accessToken) return { accessToken: "", refreshToken: "", linkError };
  if (!accessToken || params.get("type") !== "recovery") return null;
  return { accessToken, refreshToken: params.get("refresh_token") || "", linkError: "" };
};

const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState("");
  // Set when the first attempt could not save the password and a new link
  // was emailed instead. Nothing was changed, so this is NOT a success.
  const [fallbackNotice, setFallbackNotice] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();

  // Extract tenant ID from URL
  const pathParts = location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || null;

  // Extract token from URL query params
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  // Read the fallback link's recovery session ONCE, then take it out of the
  // address bar so it is not left in browser history or copied by accident.
  const [recovery] = useState<RecoveryLink | null>(() => readRecoveryLink(location.hash || ""));
  useEffect(() => {
    if (!recovery) return;
    try {
      window.history.replaceState(window.history.state, "", `${location.pathname}${location.search}`);
    } catch {
      // Leaving the fragment in place is harmless to the reset itself.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recovery]);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token && recovery?.linkError) {
        setVerifying(false);
        setError("This reset link has expired or was already used. Please request a new one.");
        return;
      }

      if (!token && !recovery) {
        setVerifying(false);
        setError("No reset token provided. Please request a new password reset link.");
        return;
      }

      try {
        const response = token
          ? await fetch(`${config.BASE_URL}/auth/verify-reset-token`, {
              method: "POST",
              headers: getHeaders(),
              body: JSON.stringify({ token }),
            })
          : await fetch(`${config.BASE_URL}/auth/verify-recovery-session`, {
              method: "POST",
              headers: getHeaders(),
              body: JSON.stringify({ accessToken: recovery?.accessToken }),
            });

        const data = await response.json();

        if (response.ok && data.valid) {
          setTokenValid(true);
          setTokenEmail(data.email || "");
        } else {
          setError(data.error || "Invalid or expired reset link. Please request a new one.");
        }
      } catch (err) {
        setError("Unable to verify reset link. Please try again or request a new one.");
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token, recovery]);

  // Sign the user straight in with the password they just set, so they never
  // have to re-type it into the login form — where the browser/password
  // manager often re-injects the OLD credential right after a reset (the
  // 2026-06-16 "reset my password, still can't get in" reports). Best-effort:
  // returns false on any hiccup so the caller can fall back to the login page.
  const autoLoginAfterReset = async (): Promise<boolean> => {
    if (!tokenEmail) return false;
    try {
      const response = await fetch(`${config.BASE_URL}/auth/login`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ email: tokenEmail, password: newPassword }),
      });
      const data = await response.json();
      if (!response.ok || !data?.token) return false;

      const { token: authToken, user, source } = data;
      safeSetItem("authToken", authToken);
      // Always write authSource (default "tenant") so a stale value can't
      // mis-tag this session's requests.
      safeSetItem("authSource", source || "tenant");

      const userData = user;
      dispatch(setUser(userData));
      const role: string | undefined = userData?.role;
      const userRole = (role || "").toLowerCase();
      safeSetItem("userRole", userRole);
      safeSetItem("userDetails", JSON.stringify(userData));

      // The admin bit comes from the server, not from users.role — see
      // Login.tsx. Staff/member stay on the role text, which is reliable for
      // that distinction.
      try {
        const caps = await getCapabilities();
        if (caps) dispatch(setAuthzFromCapabilities(caps));
      } catch {
        // Fail closed: isAdmin stays false and the backend remains authority.
      }

      // No admin branch: isStaffRole and isMemberRole both exclude ADMIN_ROLES,
      // so an admin dispatches neither — exactly as before, when the admin
      // branch set only the bit that now comes from capabilities.
      if (isStaffRole(role)) {
        dispatch(setIsStaff(userRole));
      } else if (isMemberRole(role)) {
        dispatch(setIsMember(userRole));
      }

      if (isAdminOrStaffRole(role)) {
        const home = staffHomeRoutePath(
          resolveStaffHomeRoute(userData?.staff_home_route, userData?.tenant_default_home_route),
          tenantId || "",
        );
        navigate(home);
      } else {
        navigate(`/${tenantId}/dashboard`);
      }
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // The one password rule (utils/passwordRule), the same the server uses.
    const problem = passwordProblem(newPassword);
    if (problem) {
      setError(problem);
      return;
    }

    setLoading(true);

    try {
      const response = token
        ? await fetch(`${config.BASE_URL}/auth/reset-password-with-token`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ token, newPassword }),
          })
        : await fetch(`${config.BASE_URL}/auth/reset-password-with-recovery`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({
              accessToken: recovery?.accessToken,
              refreshToken: recovery?.refreshToken,
              newPassword,
            }),
          });

      const data = await response.json();

      if (response.ok && data.success && data.usedFallback) {
        // The password was NOT changed. A new link is on its way by email;
        // say so instead of "Password updated" followed by a failed sign-in.
        setFallbackNotice(
          data.message ||
            "We could not save your new password just now, so we emailed you a new reset link. Please open that email and use the new link to finish."
        );
      } else if (response.ok && data.success) {
        setSuccess(true);
        // Auto-sign-in so the user skips the login form entirely. Fall back to
        // the login page if auto-login can't complete for any reason.
        const signedIn = await autoLoginAfterReset();
        if (!signedIn) {
          setTimeout(() => {
            navigate(`/${tenantId}/login`);
          }, 3000);
        }
      } else {
        setError(data.error || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (verifying) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar transparent={true} />
        <div className={`${getPageBackground(isDarkMode)} flex-1 flex items-center justify-center py-8 sm:py-16 px-4 sm:px-8`}>
          <div className={`${getCardBackground(isDarkMode)} py-6 px-4 sm:p-8 rounded-lg shadow-lg w-full max-w-sm sm:max-w-md text-center`}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
            <p className={`${getSecondaryText(isDarkMode)}`}>Verifying reset link...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Invalid/expired token state
  if (!tokenValid && !verifying) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar transparent={true} />
        <div className={`${getPageBackground(isDarkMode)} flex-1 flex items-center justify-center py-8 sm:py-16 px-4 sm:px-8`}>
          <div className={`${getCardBackground(isDarkMode)} py-6 px-4 sm:p-8 rounded-lg shadow-lg w-full max-w-sm sm:max-w-md`}>
            <h2 className={`text-xl sm:text-2xl font-bold text-center mb-4 sm:mb-6 ${getPrimaryText(isDarkMode)}`}>
              Reset Link Invalid
            </h2>
            <div className="bg-red-50 border-l-4 border-red-400 p-3 sm:p-4 mb-6">
              <p className="text-red-700 text-sm sm:text-base">{error}</p>
            </div>
            <Link
              to={`/${tenantId}/forgot-password`}
              className="block w-full text-center bg-[var(--cc-action)] text-[var(--cc-action-text)] py-2 px-4 sm:py-3 sm:px-6 rounded-lg hover:bg-[var(--cc-action-hover)] transition-colors duration-200 text-sm sm:text-base font-medium"
            >
              Request New Reset Link
            </Link>
            <div className="mt-4 text-center">
              <Link
                to={`/${tenantId}/login`}
                className={`text-sm ${getMutedText(isDarkMode)} hover:underline`}
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Valid token - show password reset form
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar transparent={true} />
      <div className={`${getPageBackground(isDarkMode)} flex-1 flex items-center justify-center py-8 sm:py-16 px-4 sm:px-8`}>
        <div className={`${getCardBackground(isDarkMode)} py-6 px-4 sm:p-8 rounded-lg shadow-lg w-full max-w-sm sm:max-w-md`}>
          <h2 className={`text-xl sm:text-2xl font-bold text-center mb-4 sm:mb-6 ${getPrimaryText(isDarkMode)}`}>
            Reset Your Password
          </h2>

          {tokenEmail && (
            <p className={`text-center mb-4 text-sm ${getMutedText(isDarkMode)}`}>
              Resetting password for: <span className="font-medium">{tokenEmail}</span>
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <Input
              label="New Password"
              id="newPassword"
              type="password"
              placeholder="Enter new password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              disabled={loading || success || !!fallbackNotice}
              hint={PASSWORD_RULE_TEXT}
            />
            <Input
              label="Confirm Password"
              id="confirmPassword"
              type="password"
              placeholder="Confirm new password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              disabled={loading || success || !!fallbackNotice}
            />

            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-3 sm:p-4">
                <p className="text-red-700 text-sm sm:text-base">{error}</p>
              </div>
            )}
            {fallbackNotice && (
              <div className="bg-amber-50 border-l-4 border-amber-400 p-3 sm:p-4" role="status">
                <p className="text-amber-800 text-sm sm:text-base">{fallbackNotice}</p>
              </div>
            )}
            {success && (
              <div className="bg-green-50 border-l-4 border-green-400 p-3 sm:p-4">
                <p className="text-green-700 text-sm sm:text-base">
                  Password updated successfully! Signing you in...
                </p>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              disabled={loading || success || !!fallbackNotice}
            >
              {loading ? "Resetting..." : success ? "Password Reset!" : "Reset Password"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to={`/${tenantId}/login`}
              className={`text-sm ${getMutedText(isDarkMode)} hover:underline`}
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPassword;
